import React, { useState, useEffect, useRef } from 'react';
import { useTryOn } from '../contexts/TryOnContext';
import { captureVideoFrame, uploadImage, requestCamera, stopMediaStream } from '../utils/imageUtils';
import { generateSessionId, createTryOnSession } from '../utils/firestoreUtils';
import PRODUCTS from '../data/products';
import '../styles/hologram.css';
import './Step2Capture.css';

/**
 * Step 2: Camera capture with countdown
 * Now navigates immediately to Step 3 and uploads in background
 */
function Step2Capture() {
  const { setCurrentStep, setSessionId, setCapturedImageUrl, setUploadPromise, setTryOnPromise, setCapturedBlob } = useTryOn();
  const [countdown, setCountdown] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [flash, setFlash] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Initialize camera on mount (no longer using shared stream from Step 1)
  useEffect(() => {
    let mounted = true;

    async function initCamera() {
      try {
        console.log('Initializing camera for Step 2...');
        const stream = await requestCamera();

        if (!mounted) {
          stopMediaStream(stream);
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setCameraReady(true);
          console.log('Camera ready');

          // Start countdown after 2 seconds
          setTimeout(() => {
            if (mounted) {
              startCountdown();
            }
          }, 2000);
        }
      } catch (err) {
        console.error('Camera error:', err);
        setError('Camera access denied. Please enable camera permissions.');
      }
    }

    initCamera();

    return () => {
      mounted = false;
      if (streamRef.current) {
        stopMediaStream(streamRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCountdown = () => {
    console.log('Starting countdown...');
    setCountdown(3);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(interval);
          // Capture after countdown ends
          setTimeout(() => captureAndUpload(), 500);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const captureAndUpload = async () => {
    try {
      // White flash effect
      setFlash(true);
      setTimeout(() => setFlash(false), 200);

      setUploading(true);
      console.log('Capturing image...');

      // Capture frame from video
      const blob = await captureVideoFrame(videoRef.current);
      console.log('Image captured:', blob.size, 'bytes');

      // Store blob for potential retry/debugging
      setCapturedBlob(blob);

      // Create a local object URL for immediate display (no network needed)
      const localBlobUrl = URL.createObjectURL(blob);
      setCapturedImageUrl(localBlobUrl);

      // Generate session ID now so Step 3 can use it
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      console.log('Generated session ID:', newSessionId);

      // Start background upload - store the promise so Step 3 can await it
      const uploadUrl = process.env.REACT_APP_UPLOAD_URL || '/upload';
      console.log('Starting background upload to:', uploadUrl);

      const uploadPromise = uploadImage(blob, uploadUrl).then(imageUrl => {
        console.log('Background upload complete:', imageUrl);
        // Update the URL to the real uploaded URL
        setCapturedImageUrl(imageUrl);
        return imageUrl;
      });

      // Store promise for Step 3 to await
      setUploadPromise(uploadPromise);

      // Kick off try-on generation as soon as upload finishes — runs in
      // parallel with the user filling out the form in Step 3.
      const tryOnPromise = uploadPromise.then(async (finalImageUrl) => {
        console.log('Upload done — pre-creating session and triggering generateTryOn');
        await createTryOnSession(newSessionId, finalImageUrl, PRODUCTS);

        const generateUrl = process.env.REACT_APP_GENERATE_TRYON_URL || '/generateTryOn';
        const tStart = Date.now();
        const response = await fetch(generateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            personImageUrl: finalImageUrl,
            sessionId: newSessionId,
            products: PRODUCTS
          })
        });
        if (!response.ok) {
          const bodyText = await response.text().catch(() => '');
          throw new Error(`generateTryOn failed: ${response.status} ${response.statusText} ${bodyText}`);
        }
        const data = await response.json();
        console.log(`generateTryOn finished in ${Date.now() - tStart}ms`, data);
        return data;
      }).catch(err => {
        console.error('Background try-on generation error:', err);
        throw err;
      });

      setTryOnPromise(tryOnPromise);

      // Navigate to Step 3 IMMEDIATELY - don't wait for upload
      setCurrentStep(3);

    } catch (err) {
      console.error('Capture error:', err);
      setError('Failed to capture image. Please try again.');
      setUploading(false);
    }
  };

  if (error) {
    return (
      <div className="step2-container">
        <div className="error-message">
          <h2>{error}</h2>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="step2-container-holo">
      {/* Camera frame */}
      <div className={`camera-frame-holo ${cameraReady ? 'fade-in' : ''}`}>
        <video
          ref={videoRef}
          className="camera-preview"
          autoPlay
          playsInline
          muted
        />

      </div>

      {/* White flash effect */}
      {flash && <div className="camera-flash"></div>}

      {!cameraReady && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Initializing camera...</p>
        </div>
      )}

      {countdown !== null && (
        <div className="countdown-overlay">
          <div className="countdown-number">{countdown}</div>
        </div>
      )}

      {uploading && (
        <div className="uploading-overlay">
          <div className="spinner"></div>
          <p>Processing...</p>
        </div>
      )}
    </div>
  );
}

export default Step2Capture;
